// Build-time configuration. Every value is overridable with a Vite env var so
// a staging deploy, a local `vite dev` against a laptop server, and the
// production bundle are the same code with different `VITE_*` values — the
// same shape as the Flutter client's `NAKAMA_HOST` / `NAKAMA_GRPC_PORT` /
// `NAKAMA_HTTP_PORT` dart-defines (lib/config.dart).

const env = import.meta.env;

/** True unless the build explicitly targets production. */
export const isTestVersion = env.VITE_NAKAMA_ENV !== 'prod';

/// The embed's own origin, for reference and for the availability manifest.
///
/// `play.dashstudios.tech` is the EXISTING self-hosted Cloudflare Worker that
/// already serves Dash Studios' smart links, with a path slug per title (`nh`
/// is Nouns Hunt — see `lib/config.dart`'s `linkHost`/`linkGameSlug`). Reusing
/// it means no new DNS zone, no new certificate, and a second Dash title gets
/// `/<slug>/embed/` rather than a second subdomain.
export const embedOrigin: string = env.VITE_EMBED_ORIGIN || 'https://play.dashstudios.tech';
export const embedPath: string = env.VITE_EMBED_PATH || '/nh/embed/';

/// WHERE THIS BUNDLE REACHES NAKAMA.
///
/// DEFAULT — through the Worker, on the embed's own origin:
/// `https://play.dashstudios.tech/nh/embed/nakama` (prod) or
/// `/nh/embed-dev/nakama` (dev). Not optional for a deployed embed: Nakama
/// speaks plain http/ws only, and a WebView blocks an https page from calling
/// http:// or ws:// as mixed content, so a direct connection can never sign a
/// rider in. The Worker terminates TLS and forwards to `nakama-dev`/`-prod`
/// (game-server-go `functions/linkservice/src/index.ts`, `nakamaResponse`).
/// Same-origin, so no CORS preflight per call.
///
/// OVERRIDE — set `VITE_NAKAMA_HOST` for `vite dev` against a laptop or a
/// server directly. That page is served over http, so mixed content does not
/// apply: it connects straight to the host on the dev/prod HTTP port (the
/// Flutter client's 7360 / 7350), no TLS, no path. Every piece is still
/// individually overridable.
const directConnection = Boolean(env.VITE_NAKAMA_HOST);

export const nakamaHost: string =
  env.VITE_NAKAMA_HOST || new URL(embedOrigin).hostname;

export const nakamaPort: string =
  env.VITE_NAKAMA_PORT || (directConnection ? (isTestVersion ? '7360' : '7350') : '443');

export const nakamaUseSsl: boolean =
  env.VITE_NAKAMA_SSL !== undefined ? env.VITE_NAKAMA_SSL === 'true' : !directConnection;

/// Path prefix in front of Nakama's own `/v2/...` and `/ws`.
export const nakamaPathPrefix: string =
  env.VITE_NAKAMA_PATH ??
  (directConnection ? '' : isTestVersion ? '/nh/embed-dev/nakama' : '/nh/embed/nakama');

/// What to pass as nakama-js's `port`.
///
/// nakama-js has no base-path option, but BOTH its REST client and its socket
/// build URLs as `${scheme}${host}:${port}` + `/v2/...` or `/ws?...`, so a
/// prefix appended to the port lands exactly where a base path would. Pinned by
/// `test/nakama_endpoint.test.ts` against the real library, so an upgrade that
/// changes URL building fails a test instead of silently hitting the Worker's
/// 404.
export const nakamaClientPort: string = `${nakamaPort}${nakamaPathPrefix}`;

/// Nakama's client key and HTTP key. Not secrets — they are compiled into
/// every shipped Flutter build already, and Nakama treats them as routing
/// credentials rather than authorization. Real authorization is the session
/// token minted by `partner_session_exchange`, which is server-to-server.
export const nakamaServerKey: string = env.VITE_NAKAMA_SERVER_KEY || 'defaultkey';
export const nakamaHttpKey: string = env.VITE_NAKAMA_HTTP_KEY || 'defaulthttpkey';

/// Where the host app reads the kill switch. Same file this build's
/// [embedEnabled] constant was generated from — see tool/emit_availability.mjs.
export const availabilityUrl: string = `${embedOrigin}${embedPath}availability.json`;

/// THE KILL SWITCH, baked in at build time from `embed.config.json`.
///
/// False means this deployment refuses to run a match. Authoritative, because
/// it is our code on our host: a host app that fails to read the manifest and
/// shows its tile anyway lands the player here, on the unavailable screen,
/// rather than in a game we have turned off.
export const embedEnabled: boolean = __EMBED_ENABLED__;
export const embedDisabledMessage: string = __EMBED_DISABLED_MESSAGE__;

/// Which partner to assume when the launch URL does not say.
///
/// The launch URL's `partner` field is authoritative; this is only a fallback
/// for a host that predates the field. It exists so ONE deployment of this
/// renderer can serve every partner — a new host is a server registry row plus
/// a launch URL, never a fork of this bundle.
export const defaultPartner: string = env.VITE_DEFAULT_PARTNER || 'shuttlers';

/// The RPC that turns a partner one-time code into a Nakama session.
/// Registered in `game-server-go/main.go`.
export const partnerSessionExchangeRpc = 'partner_session_exchange';

/// Where an "open the full game" hand-off sends the player. Carries the Play
/// Store `referrer` click id so installs attribute back to the embed.
export const fullGameUrl: string =
  env.VITE_FULL_GAME_URL ||
  'https://play.google.com/store/apps/details?id=tech.dashstudios.nouns_flutter&referrer=utm_source%3Dshuttlers%26utm_medium%3Dembed';
